import { useState, useEffect, useMemo, useCallback } from 'react';
import AuthGuard from '@/components/feature/AuthGuard';
import AdminLayout from '../components/AdminLayout';
import { supabase } from '@/lib/supabase';

interface FacultyRecord {
  id: string;
  name: string;
  designation: string;
  department: string;
  college_name: string;
  contact_number: string;
  email: string;
  team_name: string;
}

export default function FacultyMentors() {
  const [mentors, setMentors] = useState<FacultyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [search, setSearch] = useState('');
  const [collegeFilter, setCollegeFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const { data: ments, error: mentErr } = await supabase.from('faculty_mentors').select('id, name, designation, department, college_name, contact_number, email, team_id').order('name');
      if (mentErr) throw mentErr;

      const { data: teams, error: teamsErr } = await supabase.from('teams').select('id, team_name');
      if (teamsErr) throw teamsErr;

      const teamMap: Record<string, string> = {};
      (teams || []).forEach((t) => { teamMap[t.id] = t.team_name; });

      setMentors((ments || []).map((m) => ({ ...m, team_name: teamMap[m.team_id] || 'Unknown Team' })));
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const colleges = useMemo(() => [...new Set(mentors.map((m) => m.college_name))].sort(), [mentors]);

  const filtered = useMemo(() => {
    let result = mentors;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((m) => m.name.toLowerCase().includes(q) || m.department.toLowerCase().includes(q) || m.college_name.toLowerCase().includes(q));
    }
    if (collegeFilter) result = result.filter((m) => m.college_name === collegeFilter);
    return result;
  }, [mentors, search, collegeFilter]);

  return (
    <AuthGuard requiredRole={['admin', 'organizer']}>
      <AdminLayout title="Faculty Mentors" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/admin' }, { label: 'Faculty Mentors' }]}>
        {fetchError && (
          <div className="mb-5 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
            Failed to load: {fetchError}. <button onClick={fetchData} className="underline font-medium cursor-pointer">Retry</button>
          </div>
        )}

        <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5 mb-5">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="relative w-full sm:w-72">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, department..." className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none" />
            </div>
            <select value={collegeFilter} onChange={(e) => setCollegeFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-primary-400 outline-none cursor-pointer">
              <option value="">All Colleges</option>
              {colleges.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <p className="text-xs text-foreground-400 mt-3">{loading ? 'Loading...' : `${filtered.length} mentor${filtered.length !== 1 ? 's' : ''}`}</p>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div></div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-foreground-400"><i className="ri-inbox-line text-3xl block mb-2"></i>No faculty mentors yet</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
              {filtered.map((m) => (
                <div key={m.id} className="p-4 rounded-xl border border-background-200/70 bg-background-50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-secondary-600">{m.name[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{m.name}</p>
                      <p className="text-xs text-foreground-500">{m.designation}</p>
                    </div>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex items-center gap-1.5 text-foreground-600"><i className="ri-building-line text-foreground-400"></i>{m.college_name}</div>
                    <div className="flex items-center gap-1.5 text-foreground-600"><i className="ri-flask-line text-foreground-400"></i>{m.department}</div>
                    <div className="flex items-center gap-1.5 text-foreground-600"><i className="ri-group-line text-foreground-400"></i>{m.team_name}</div>
                    <div className="flex items-center gap-1.5 text-foreground-600"><i className="ri-phone-line text-foreground-400"></i>{m.contact_number}</div>
                    <div className="flex items-center gap-1.5 text-foreground-500"><i className="ri-mail-line text-foreground-400"></i>{m.email}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </AdminLayout>
    </AuthGuard>
  );
}